import { skipToken } from '@reduxjs/toolkit/query';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordMoreMenu,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetEffortByIdQuery } from 'store/api/efforts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useConfirmDeleteEffort } from './useConfirmDeleteEffort';
import GeneralView from './views/GeneralView';

const USED_CLASSIFIERS = ['EFFORT_ACTIVITY_TYPE', 'EXPENSE_TYPE', 'CURRENCY', 'LEGAL_DIRECTORY'];
const UPDATE_PERMISSIONS = ['UPDATE.EFFORTS.ALL', 'UPDATE.EFFORTS.MY_OWN'];
const DELETE_PERMISSIONS = ['DELETE.EFFORTS.ALL', 'DELETE.EFFORTS.MY_OWN'];

const EffortPage = (): JSX.Element => {
    const { id: effortId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/efforts');
    const isNew = useIsNewDesign();
    const canUpdate = useHasPermissions(UPDATE_PERMISSIONS);
    const canDelete = useHasPermissions(DELETE_PERMISSIONS);
    const confirmDelete = useConfirmDeleteEffort();

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: effort, isLoading: isLoadingEffort } = useGetEffortByIdQuery(effortId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!effort?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/efforts');
    const draftActions = useDraftActions({
        draft: effort?.isDraft ? { type: 'bd-effort', id: effort.id } : null,
        createRoute: '/effort/new',
        onDeleted: leaveDeleted,
    });
    const { draft } = draftActions;
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const tabs = useMemo<TabType[]>(() => {
        if (!effort) return [];
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: <GeneralView effort={effort} classifiers={classifiers} isNew={isNew} />,
            },
        ];
    }, [effort, classifiers, isNew]);

    if (isLoadingEffort || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!effort) {
        return <></>;
    }

    const activityTypeLabel = effort.effortActivityTypeCode
        ? getClassifierText(classifiers['EFFORT_ACTIVITY_TYPE'], effort.effortActivityTypeCode)
        : '';
    const title = activityTypeLabel || t('efforts.bd-effort');

    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    // The General view reads the draft from here: what it misses, and whom it is shared with.
    const tabsGallery = (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <TabsGallery
                tabs={tabs}
                tabGroupId="effort-page"
                trackTabsInHistoryState
                usePathBasedRouting
                wrapperClassName={RECORD_TABS_CLASS}
                panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
                // The new design's white band runs from the header through the tab strip, edge to edge.
                className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
            />
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );

    if (isNew) {
        // Edit went to the sections; Delete, which only the edit form had, moves to the ⋯ menu.
        return (
            <RecordPage
                header={
                    isDraft ? (
                        <div className="flex flex-col gap-4">
                            <RecordHeader
                                title={title}
                                chips={<DraftChip />}
                                meta={draft ? <DraftMeta draft={draft} /> : undefined}
                                actions={<DraftRecordActions actions={draftActions} />}
                            />
                            {draftNotice}
                        </div>
                    ) : (
                        <RecordHeader
                            title={title}
                            pin={{ kind: 'effort', id: effort.id }}
                            actions={
                                <RecordMoreMenu
                                    menuItems={
                                        canDelete
                                            ? [
                                                  {
                                                      title: t('delete'),
                                                      variant: 'destructive',
                                                      disabled: !effort.isDeletable,
                                                      handler: () => confirmDelete(String(effort.id)),
                                                  },
                                              ]
                                            : []
                                    }
                                />
                            }
                        />
                    )
                }
            >
                <SectionEditProvider canEdit={!isDraft && !!effort.isEditable && canUpdate}>
                    {tabsGallery}
                </SectionEditProvider>
            </RecordPage>
        );
    }

    return (
        <PaddedLayout className="flex h-full flex-col overflow-hidden">
            <PageHeader
                title={title}
                showBreadcrumb
                onBack={goBack}
                buttons={
                    isDraft ? (
                        <div className="flex items-center justify-end gap-2">
                            <DraftRecordActions actions={draftActions} />
                        </div>
                    ) : effort.isEditable ? (
                        <PermissionButton
                            permissions={UPDATE_PERMISSIONS}
                            variant="outlined"
                            size="small"
                            onClick={() => navigateWithReturn(`/effort/${effortId}/edit`)}
                        >
                            <FormattedMessage id="edit" />
                        </PermissionButton>
                    ) : undefined
                }
            >
                {draft ? (
                    <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                        <DraftChip />
                        <DraftMeta draft={draft} />
                    </div>
                ) : null}
            </PageHeader>
            {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
            {tabsGallery}
        </PaddedLayout>
    );
};

export default EffortPage;
